import AppIconButton from "../form/AppIconButton";
import AppMenuItem from "../form/AppMenuItem";
import { useState } from "react";
import { CircularProgress, ListItemIcon, ListItemText, Menu, Tooltip } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { useTranslation } from "react-i18next";
import ExportScheduleDialog from "../ExportScheduleDialog";
import { dashboardExportService } from "../../services/dashboardExportService";
import type { DateRangeValue } from "../DateRangeHeader";
import { safeExportFileName } from "../../utils/exportFileName";
import {
  DASHBOARD_EXPORT_SURFACE_ID,
  downloadDashboardPdf,
  printDashboardSurface,
} from "../../utils/dashboardCanvasExport";

type Props = {
  dashboardId: string;
  dashboardName?: string;
  dateRange: DateRangeValue;
  shareToken?: string;
  disabled?: boolean;
  /** DOM id of the live grid/canvas to capture for PDF/print. */
  surfaceId?: string;
};

export default function DashboardExportMenu({
  dashboardId,
  dashboardName = "dashboard",
  dateRange,
  shareToken,
  disabled = false,
  surfaceId = DASHBOARD_EXPORT_SURFACE_ID,
}: Props) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState<"pdf" | "xlsx" | "print" | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const close = () => setAnchor(null);

  const runPdf = async () => {
    setExporting("pdf");
    try {
      const fileName = safeExportFileName(dashboardName, "pdf");
      await downloadDashboardPdf({
        fileName,
        surfaceId,
        title: dashboardName,
      });
    } catch (error) {
      console.error(error);
      // Fallback to server PDF if canvas capture fails
      const fileName = safeExportFileName(dashboardName, "pdf");
      await dashboardExportService.downloadPdf(
        dashboardId,
        dateRange.from,
        dateRange.to,
        fileName,
        shareToken
      );
    } finally {
      setExporting(null);
      close();
    }
  };

  const runExcel = async () => {
    setExporting("xlsx");
    try {
      const fileName = safeExportFileName(dashboardName, "xlsx");
      await dashboardExportService.downloadExcel(
        dashboardId,
        dateRange.from,
        dateRange.to,
        fileName,
        shareToken
      );
    } finally {
      setExporting(null);
      close();
    }
  };

  const runPrint = async () => {
    setExporting("print");
    try {
      await printDashboardSurface({ surfaceId, title: dashboardName });
    } catch (error) {
      console.error(error);
      window.print();
    } finally {
      setExporting(null);
      close();
    }
  };

  return (
    <>
      <Tooltip title={t("export.menu")}>
        <span>
          <AppIconButton
            size="small"
            className="no-print"
            busyIgnore
            disabled={disabled || !!exporting}
            onClick={(e) => setAnchor(e.currentTarget)}
            aria-label={t("export.menu")}
          >
            {exporting ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon fontSize="small" />}
          </AppIconButton>
        </span>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <AppMenuItem onClick={() => void runPdf()} disabled={!!exporting}>
          <ListItemIcon>
            <PictureAsPdfOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("export.pdf")}</ListItemText>
        </AppMenuItem>
        <AppMenuItem onClick={() => void runExcel()} disabled={!!exporting}>
          <ListItemIcon>
            <TableChartOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("export.excel")}</ListItemText>
        </AppMenuItem>
        <AppMenuItem
          busyIgnore
          onClick={() => {
            close();
            setScheduleOpen(true);
          }}
        >
          <ListItemIcon>
            <ScheduleOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("export.schedule")}</ListItemText>
        </AppMenuItem>
        <AppMenuItem busyIgnore onClick={() => void runPrint()} disabled={!!exporting}>
          <ListItemIcon>
            <PrintOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("export.print")}</ListItemText>
        </AppMenuItem>
      </Menu>
      <ExportScheduleDialog
        open={scheduleOpen}
        dashboardId={dashboardId}
        dashboardName={dashboardName}
        onClose={() => setScheduleOpen(false)}
      />
    </>
  );
}
